import { navigate } from 'gatsby';
import { DateTime } from 'luxon';
import { T, always, cond, equals, or } from 'ramda';
import React, { FC, useState } from 'react';
import styled from 'styled-components';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import FlexRow, { IFlexRow } from '@ui/atoms/FlexRow';
import HeadingText from '@ui/atoms/HeadingText';
import NavigationSection from '@ui/atoms/NavigationSection';
import TextLink from '@ui/atoms/TextLink';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import Checkbox, { ICheckboxProps } from '@ui/forms/Checkbox';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import SubmitButton from '@ui/molecules/SubmitButton';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';

import { interpolate } from '@utils/butter';
import { commonLabels } from '@utils/button';
import { exists } from '@utils/general';
import { centsToDollars } from '@utils/money';

import { InsuranceCoverage } from '@data/insurance';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';
import { IVisitPrice } from '@folx-types/insurance';

import theme from '@theme/FolxTheme';

import { dataConstants } from './InsuranceOrderSummaryStep.data';

export interface IInsuranceOrderSummaryStepPageContent {
	inNetworkHeader: string;
	outOfNetworkHeader: string;
	visitHeader: string;
	outOfNetworkDescription: string;
	payNowText: string;
	expectLaterText: string;
	oufOfPocketText: string;
	copayText: string;
	copayDescription: string;
	depositText: string;
	depositDescription: string;
	subsequentAppointmentText: string;
	initialAppointmentText: string;
	initialAppointmentDescription: string;
	checkoutText: string;
	loadingHeader: string;
	loadingHeaderNoInsurance: string;
	therapyAppointmentText: string;
	firstMedicationAppointmentText: string;
	followupMedicationAppointmentText: string;
	firstMedicationAppointmentDescription: string;
	appointmentErrorHeader: string;
	invalidAppointmentText: string;
	invalidAppointmentButtonText: string;
	pastCancelDateAppointmentText: string;
	reactivateMembershipLabel: string;
	assignmentOfBenefits: string;
}

interface IInsuranceOrderSummaryStepProps extends IFlowStepComponentProps {
	pastCancelDateAppointmentError: boolean;
	invalidAppointmentError: boolean;
	pageContent: IInsuranceOrderSummaryStepPageContent;
	cancelDate?: DateTime;
	submitting?: boolean;
	submitToStripe?: () => void;
	topicId?: string;
	visitPrice?: IVisitPrice;
}

const AlignedRow = aligned<IFlexRow>(FlexRow);

const CheckoutNavigationSection = styled(NavigationSection)`
	flex-direction: column-reverse;

	@media ${theme.responsive.breakpoints.tablet} {
		flex-direction: row;
		${Button} {
			max-width: 24rem;
		}
	}
`;

const InsuranceOrderSummaryStep: FC<IInsuranceOrderSummaryStepProps> = ({
	cancelDate = DateTime.now(),
	destinationFlow,
	handleNavBack = () => {
		return;
	},
	invalidAppointmentError,
	pageContent,
	pastCancelDateAppointmentError,
	stepKey,
	submitting,
	submitToStripe = () => {
		return;
	},
	visitPrice,
}) => {
	const {
		inNetworkHeader,
		outOfNetworkHeader,
		visitHeader,
		outOfNetworkDescription,
		payNowText,
		expectLaterText,
		oufOfPocketText,
		copayText,
		copayDescription,
		depositText,
		depositDescription,
		subsequentAppointmentText,
		initialAppointmentText,
		initialAppointmentDescription,
		checkoutText,
		therapyAppointmentText,
		firstMedicationAppointmentText,
		followupMedicationAppointmentText,
		firstMedicationAppointmentDescription,
		appointmentErrorHeader,
		invalidAppointmentText,
		invalidAppointmentButtonText,
		pastCancelDateAppointmentText,
		reactivateMembershipLabel,
		assignmentOfBenefits,
	} = pageContent;

	const [checked, setChecked] = useState(false);

	const { insuranceCoverageTypes, appointmentTypes } = dataConstants;
	const insuranceCoverageType = visitPrice?.insuranceCoverage ?? '';
	const appointmentType = visitPrice?.productType ?? '';

	const isInNetwork = (coverageType: InsuranceCoverage | '') =>
		or(
			equals(insuranceCoverageTypes.inNetworkWithCopay, coverageType),
			equals(insuranceCoverageTypes.inNetworkNoCopay, coverageType)
		);

	const isOutOfNetwork = (coverageType?: InsuranceCoverage | '') =>
		or(
			equals(insuranceCoverageTypes.outOfNetwork, coverageType),
			equals(insuranceCoverageTypes.cashPay, coverageType)
		);

	const header = cond([
		[(v) => isInNetwork(v), always(inNetworkHeader)],
		[equals(insuranceCoverageTypes.outOfNetwork), always(outOfNetworkHeader)],
		[equals(insuranceCoverageTypes.cashPay), always(visitHeader)],
		[T, always(visitHeader)],
	])(insuranceCoverageType);

	const paymentHeader = cond([
		[(v) => isInNetwork(v), always(payNowText)],
		[(v) => isOutOfNetwork(v), always(oufOfPocketText)],
		[T, always(oufOfPocketText)],
	])(insuranceCoverageType);

	const appointmentText = cond([
		[equals(appointmentTypes.initial), always(initialAppointmentText)],
		[equals(appointmentTypes.subsequent), always(subsequentAppointmentText)],
		[equals(appointmentTypes.initialMedication), always(firstMedicationAppointmentText)],
		[equals(appointmentTypes.subsequentMedication), always(followupMedicationAppointmentText)],
		[equals(appointmentTypes.initialTherapy), always(therapyAppointmentText)],
		[equals(appointmentTypes.therapy), always(therapyAppointmentText)],
		[T, always(subsequentAppointmentText)],
	])(appointmentType);

	const paymentText = cond([
		[equals(insuranceCoverageTypes.inNetworkWithCopay), always(copayText)],
		[equals(insuranceCoverageTypes.inNetworkNoCopay), always(depositText)],
		[(v) => isOutOfNetwork(v), always(appointmentText)],
		[T, always(appointmentText)],
	])(insuranceCoverageType);

	const appointmentDescription = cond([
		[equals(appointmentTypes.initial), always(initialAppointmentDescription)],
		[equals(appointmentTypes.initialMedication), always(firstMedicationAppointmentDescription)],
		[T, always('')],
	])(appointmentType);

	const paymentDescription = cond([
		[equals(insuranceCoverageTypes.inNetworkWithCopay), always(copayDescription)],
		[equals(insuranceCoverageTypes.inNetworkNoCopay), always(depositDescription)],
		[(v) => isOutOfNetwork(v), always(appointmentDescription)],
		[T, always(appointmentDescription)],
	])(insuranceCoverageType);

	const appointmentErrorDescription = cond([
		[equals(invalidAppointmentError), () => <BasicText>{invalidAppointmentText}</BasicText>],
		[
			equals(pastCancelDateAppointmentError),
			() => (
				<WYSIWYGContent
					dangerouslySetInnerHTML={{
						__html: interpolate(pastCancelDateAppointmentText, {
							cancelDate: cancelDate.toFormat('EEEE, MMMM d'),
						}),
					}}
				/>
			),
		],
	])(true);

	const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
	const AlignedColumn = aligned<IFlexColumn>(FullWidthColumn);
	const MaxWidthButton = styled(Button)`
		max-width: 32rem;
		width: 100%;
		margin: ${theme.spacing.sm} auto 0;
	`;

	const AppointmentErrorView = () => (
		<AlignedColumn gap="sm" alignItems="center">
			<BaseCard
				borderColor="red"
				bgColor="white"
				body={
					<FlexColumn gap="sm">
						<HeadingText>{appointmentErrorHeader}</HeadingText>
						{appointmentErrorDescription}
						<MaxWidthButton
							variant="secondary"
							label={invalidAppointmentButtonText}
							labelSize="xs"
							onClick={() => {
								navigate('../appointment_selection');
							}}
							radius="xxxl"
						/>
					</FlexColumn>
				}
				radius="xxxxl"
				shadow
			/>
			{pastCancelDateAppointmentError && (
				<TextLink label={reactivateMembershipLabel} href="/app/member/reactivate_membership" />
			)}
		</AlignedColumn>
	);

	const AppointmentCheckoutView = () => (
		<FlexColumn gap="lg">
			<HeadingText>{header}</HeadingText>
			{insuranceCoverageType === insuranceCoverageTypes.outOfNetwork && (
				<WYSIWYGContent dangerouslySetInnerHTML={{ __html: outOfNetworkDescription }} />
			)}
			<FlexColumn gap="sm">
				<BasicText variant="field">{paymentHeader}</BasicText>
				<AlignedRow justifyContent="space-between">
					<BasicText>{paymentText}</BasicText>
					<BasicText>{centsToDollars(visitPrice?.dueNow ?? 0)}</BasicText>
				</AlignedRow>
			</FlexColumn>
			{isInNetwork(insuranceCoverageType) && (
				<BasicText variant="field">{expectLaterText}</BasicText>
			)}
			{exists(paymentDescription) && (
				<WYSIWYGContent dangerouslySetInnerHTML={{ __html: paymentDescription }} />
			)}
			{isInNetwork(insuranceCoverageType) && (
				<Checkbox
					alignment="flex-start"
					textSize="xs"
					name="assignmentOfBenefits"
					onClick={(event) => setChecked(event.target.checked)}
					checked={checked}
					label={assignmentOfBenefits}
				/>
			)}
			<CheckoutNavigationSection>
				<Button label={commonLabels.back} onClick={() => handleNavBack(stepKey)} />
				<SubmitButton
					disabled={isInNetwork(insuranceCoverageType) && !checked}
					variant="secondary"
					submitting={submitting}
					label={checkoutText}
					onClick={() => submitToStripe(checked)}
				/>
			</CheckoutNavigationSection>
		</FlexColumn>
	);

	return (
		<CenteredGridItem>
			{invalidAppointmentError || pastCancelDateAppointmentError ? (
				<AppointmentErrorView />
			) : (
				<AppointmentCheckoutView />
			)}
		</CenteredGridItem>
	);
};

export default InsuranceOrderSummaryStep;
